import { displayTz } from "@/lib/prefs";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, errorText } from "@/lib/api";
import { readSlate, writeSlate } from "@/lib/cache";
import { nbaToday, tipoff } from "@/lib/format";
import { PageTitle, Panel } from "@/components/AppShell";
import { GamePills } from "@/components/today/GamePills";
import { SlateToolbar } from "@/components/today/SlateToolbar";
import { PlayerStatsTable } from "@/components/playerrow/PlayerStatRow";
import { DateStrip } from "@/components/today/DateStrip";
import { LastNightButton } from "@/components/today/LastNight";
import { toast } from "sonner";
import { LineupPanel } from "@/components/today/LineupPanel";
import { StatFilterBar } from "@/components/playerrow/RecentLog";
import { LiveRosterArena } from "@/components/arena/LiveRosterArena";
import { useAuth } from "@/context/AuthContext";
import { SyncStatus } from "@/components/SyncStatus";

function sortValue(row, key) {
  if (key === "name") return row.name;
  if (key === "form") return row.form?.pts?.pct ?? -999;
  if (key === "min") return row.min ?? -1;
  if (row.mode === "final") return row.actual?.[key] ?? -1;
  return row.stats?.[key]?.proj ?? -1;
}

// OUT = ESPN injury "Out" or RotoWire chance to play 0%.
const isOut = (r) => r.play_pct === 0 || /out/i.test(r.injury || "");
const TOP_STATS = ["pts", "min", "reb", "ast", "fg3m", "pra"];

const projPts = (totals, g, teamId) => totals.find((t) => t.game_id === g.id && t.team_id === teamId && t.mode === "rotation")?.totals?.pts;

// Short notice when a game goes final: final score vs the projection.
function notifyFinal(g, totals) {
  const pa = projPts(totals, g, g.away_team_id);
  const ph = projPts(totals, g, g.home_team_id);
  const title = `FINAL · ${g.away_code} ${g.away_pts}-${g.home_pts} ${g.home_code}`;
  if (pa == null || ph == null) { toast(title, { duration: 12000 }); return; }
  const off = Math.round(pa + ph - (g.away_pts + g.home_pts));
  const winner = (pa > ph) === (g.away_pts > g.home_pts);
  toast(title, {
    duration: 12000,
    description: `Projection ${Math.round(pa)}-${Math.round(ph)} · total ${off > 0 ? "+" : ""}${off} · winner ${winner ? "right" : "wrong"}`,
  });
}

export default function Today() {
  const [day, setDay] = useState(nbaToday());
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [game, setGame] = useState("all");
  const [team, setTeam] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "pts", dir: -1 });
  const [logStat, setLogStat] = useState("pts");
  const [livePlayers, setLivePlayers] = useState({});
  const { user } = useAuth();
  const [syncInfo, setSyncInfo] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const loadSyncStatus = () => api.get("/sync-status").then((r) => setSyncInfo(r.data.nba)).catch(() => {});
  useEffect(() => { loadSyncStatus(); }, []);

  const refreshNow = async () => {
    setSyncing(true);
    const before = syncInfo?.last_sync;
    try {
      await api.post("/teams/sync");
      toast.success("Ξεκίνησε η ανανέωση δεδομένων…");
      for (let i = 0; i < 24; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const r = await api.get("/sync-status");
        setSyncInfo(r.data.nba);
        if (r.data.nba?.last_sync && r.data.nba.last_sync !== before) {
          const p = await api.get("/projections", { params: { day } });
          setData(p.data); writeSlate(day, p.data);
          toast.success("Τα δεδομένα ανανεώθηκαν");
          break;
        }
      }
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSyncing(false);
    }
  };

  const [startersOnly, setStartersOnly] = useState(false);
  const [showOut, setShowOut] = useState(false);
  const [top10, setTop10] = useState(false);
  const today = nbaToday();
  // Live games running: the pregame filter bar is hidden, so its hidden filters are cleared too.
  const liveMode = Object.keys(livePlayers).length > 0;
  useEffect(() => {
    if (liveMode) { setQ(""); setStartersOnly(false); setShowOut(false); setTop10(false); }
  }, [liveMode]);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/projections", { params: { day } })
      .then((r) => { if (alive) { setData(r.data); writeSlate(day, r.data); } })
      .catch((e) => alive && setError(errorText(e)));
    const checkLineups = () => api.post("/lineups/refresh", null, { params: { day } })
      .then((r) => { if (r.data.announced > 0) load(); }).catch(() => {});
    setData(readSlate(day)); setError(""); setGame("all"); setTeam("all"); setLivePlayers({});
    load().then(checkLineups);
    const timer = setInterval(checkLineups, 300000);
    return () => { alive = false; clearInterval(timer); };
  }, [day]);

  const pending = (data?.games || []).some((g) => g.status === "pre" || g.status === "in");
  const isUnderway = (g) => g.status === "in" || (g.status === "pre" && new Date(g.date_utc) <= new Date());
  const underway = (data?.games || []).some(isUnderway);
  const gamesRef = useRef([]);
  gamesRef.current = data?.games || [];
  const totalsRef = useRef([]);
  totalsRef.current = data?.team_totals || [];
  useEffect(() => {
    if (!pending) return undefined;
    let alive = true;
    const tick = () => {
      const games = gamesRef.current;
      if (!games.some(isUnderway)) return;
      api.get("/live", { params: { day } }).then((r) => {
        if (!alive) return;
        const live = Object.fromEntries(r.data.games.map((g) => [g.id, g]));
        setLivePlayers(r.data.players || {});
        const ended = games.filter((g) => g.status !== "post" && live[g.id]?.status === "post");
        ended.forEach((g) => notifyFinal({ ...g, ...live[g.id] }, totalsRef.current));
        if (ended.length) {
          api.get("/projections", { params: { day } }).then((p) => { if (alive) { setData(p.data); writeSlate(day, p.data); } }).catch(() => {});
        } else {
          setData((d) => d && { ...d, games: d.games.map((g) => (live[g.id] ? { ...g, ...live[g.id] } : g)) });
        }
      }).catch(() => {});
    };
    tick();
    const timer = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(timer); };
  }, [day, pending, underway]); // eslint-disable-line react-hooks/exhaustive-deps

  const teams = useMemo(() => [...new Set((data?.rows || []).map((r) => r.team))].sort(), [data]);
  const topKey = TOP_STATS.includes(sort.key) ? sort.key : "pts";
  const rows = useMemo(() => {
    const anyGame = top10 || game === "all", anyTeam = top10 || team === "all";
    const list = (data?.rows || []).map((r) => (livePlayers[r.game_id] ? { ...r, live: livePlayers[r.game_id][r.player_id] || {} } : r)).filter((r) =>
      // Show OUT = only the OUT players of the current selection; otherwise OUT players are hidden.
      (showOut ? r.mode !== "final" && isOut(r) : r.mode === "final" || !isOut(r)) &&
      (startersOnly ? r.starter : showOut || r.stats || r.mode === "final") && (anyGame || r.game_id === game) && (anyTeam || r.team === team) &&
      r.name.toLowerCase().includes(q.toLowerCase()));
    // Top 10: the day's best 10 in the chosen category, across every game and team.
    if (top10) return list.sort((a, b) => sortValue(b, topKey) - sortValue(a, topKey)).slice(0, 10);
    return list.sort((a, b) => {
      const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
      return (va > vb ? 1 : va < vb ? -1 : 0) * sort.dir;
    });
  }, [data, game, team, q, sort, startersOnly, showOut, livePlayers, top10, topKey]);
  const outCount = (data?.rows || []).filter((r) => r.mode !== "final" && isOut(r) && (game === "all" || r.game_id === game) && (team === "all" || r.team === team)).length;
  const selectedGame = data?.games?.find((g) => g.id === game);
  const liveRows = useMemo(() => rows.filter((r) => r.live), [rows]);
  const otherRows = liveRows.length ? rows.filter((r) => !r.live) : rows;

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : key === "name" ? 1 : -1 }));

  return (
    <div>
      <PageTitle overline={data?.games?.length && data.games.every((g) => g.status === "post") ? "Final results · actual stats" : "Pregame projections"} title={day === today ? "Today's slate" : "NBA slate"}>
        <LastNightButton today={today} />
      </PageTitle>
      <DateStrip value={day} onChange={setDay} today={today} />
      {syncInfo && <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <SyncStatus lastSync={syncInfo.last_sync} onRefresh={refreshNow} busy={syncing} canRefresh={user?.role === "admin"} testid="today-sync" />
      </div>}
      {error && <p data-testid="today-error" className="text-rose-400 mb-4">{error}</p>}
      <GamePills games={data?.games || []} value={game} onChange={(g) => { setGame(g); setTeam("all"); }} teamTotals={data?.team_totals} loading={!data && !error} />
      {data && !data.games.length && <p data-testid="no-games" className="text-slate-400">No NBA games on this date.</p>}
      {selectedGame && <LineupPanel game={selectedGame} team={team} onTeam={(t) => setTeam((cur) => (cur === t ? "all" : t))} />}
      {selectedGame && !liveRows.length && <StatFilterBar value={logStat} onChange={setLogStat} />}
      <Panel className="mt-6">
        {!liveMode && <SlateToolbar q={q} setQ={setQ} team={team} setTeam={setTeam} teams={teams}
          startersOnly={startersOnly} setStartersOnly={setStartersOnly} showOut={showOut} setShowOut={setShowOut} outCount={outCount} count={rows.length} espn={data?.rosters_from_espn}
          top10={top10} setTop10={(on) => { setTop10(on); if (on) setSort({ key: topKey, dir: -1 }); }} topKey={topKey} />}
        <div className="px-3 pb-3">
          {liveRows.length > 0 && <LiveRosterArena rows={liveRows} />}
          {liveRows.length > 0 && otherRows.length > 0 && <h3 className="font-display text-lg font-extrabold uppercase text-slate-300 mt-8 mb-2" data-testid="other-games-title">Other games</h3>}
          {otherRows.length > 0 && <PlayerStatsTable rows={otherRows} sort={sort} onSort={toggleSort} minGames={data?.min_games} logStat={selectedGame ? logStat : null}
            totals={top10 ? false : { teamTotals: data?.team_totals, game, team, subset: !!q || startersOnly }} />}
          {data && data.games.length > 0 && !rows.length && (
            <p data-testid="today-empty" className="p-8 text-slate-400">
              No players for this selection yet.
            </p>
          )}
          {!data && !error && <p className="p-8 text-slate-500" data-testid="today-loading">Loading projections…</p>}
        </div>
      </Panel>
      <p className="text-xs text-slate-500 mt-3">Tip-off times in your timezone ({displayTz().replace(/_/g, " ")}). {data?.games?.[0] && `First game ${tipoff(data.games[0].date_utc)}.`}</p>
    </div>
  );
}
